import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  collection,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { normalizeAddress } from './addressUtils';

/**
 * @deprecated After multi-chain migration, user registration is handled in walletService.js
 * This function is kept for backward compatibility but does nothing.
 */
export const registerUser = async (address) => {
  console.warn('[DEPRECATED] registerUser is deprecated. Registration is now handled in walletService.js');
  // No-op: Registration is now handled by walletService.js setting registered=true
};

/**
 * Check if a user is registered (has connected wallet before)
 * @param {string} address - Plain address (backward compatibility)
 * @param {string} chain - Chain name (defaults to 'ethereum')
 * @returns {Promise<boolean>} True if user has connected before
 */
export const isUserRegistered = async (address, chain = 'ethereum') => {
  try {
    const uid = normalizeAddress(address);
    const userRef = doc(db, 'addresses', uid);
    const userDoc = await getDoc(userRef);
    // User is registered if they have a registeredAt timestamp
    return userDoc.exists() && userDoc.data().registeredAt != null;
  } catch (error) {
    console.error('Error checking if user is registered:', error);
    return false;
  }
};

/**
 * Get watched addresses for the current user
 * @returns {Promise<Array<string>>} Array of watched addresses
 */
export const getWatchedAddresses = async () => {
  // Check authentication
  if (!auth.currentUser) return [];

  const compositeId = auth.currentUser.uid;

  try {
    // New path: addresses/{compositeId}/watched
    const watchedRef = collection(db, 'addresses', compositeId, 'watched');
    const q = query(watchedRef, orderBy('watchedAt', 'desc'));
    const snapshot = await getDocs(q);

    // Return plain addresses for backward compatibility
    // Filter out coins (which have type='coin' and no targetAddress)
    return snapshot.docs
      .map(doc => doc.data())
      .filter(data => data.targetAddress) // Only items with targetAddress (addresses, not coins)
      .map(data => data.targetAddress);
  } catch (error) {
    console.error('Error getting watched addresses:', error);
    return [];
  }
};

// Cloud Function for watched item operations (unified for addresses and coins)
const manageWatchingFn = httpsCallable(functions, 'manageWatching');

/**
 * Add an address to watch list via Cloud Function
 * Chain-agnostic: same address across all EVM chains is treated as one entry
 * Server-side enforcement of max 4 items limit
 * @param {string} addressToWatch - The address to add to watch list
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const addWatchedAddress = async (addressToWatch) => {
  if (!auth.currentUser) {
    return { success: false, error: 'You must be signed in to add watched addresses' };
  }

  try {
    const result = await manageWatchingFn({
      op: 'add',
      type: 'address',
      address: addressToWatch
    });
    return result.data;
  } catch (error) {
    console.error('Error adding watched address:', error);
    const errorMessage = error.message || 'Failed to add address';
    return { success: false, error: errorMessage };
  }
};

/**
 * Remove an address from watch list via Cloud Function
 * Chain-agnostic: same address across all EVM chains is treated as one entry
 * @param {string} addressToRemove - The address to remove from watch list
 */
export const removeWatchedAddress = async (addressToRemove) => {
  if (!auth.currentUser) {
    throw new Error('You must be signed in to remove watched addresses');
  }

  try {
    const result = await manageWatchingFn({
      op: 'remove',
      type: 'address',
      address: addressToRemove
    });
    return result.data;
  } catch (error) {
    console.error('Error removing watched address:', error);
    throw error;
  }
};

export const MAX_DISPLAY_NAME_LENGTH = 50;
export const MAX_WATCHED_ITEMS = 4;  // Combined limit for addresses + coins
export const MAX_WATCHED_ADDRESSES = MAX_WATCHED_ITEMS;  // Backward compatibility

/**
 * Get all watched items (addresses + coins) for the current user
 * @returns {Promise<Array<{type: string, ...}>>} Array of watched items with type field
 */
export const getWatchedItems = async () => {
  if (!auth.currentUser) return [];

  const compositeId = auth.currentUser.uid;

  try {
    const watchedRef = collection(db, 'addresses', compositeId, 'watched');
    const q = query(watchedRef, orderBy('watchedAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => {
      const data = doc.data();
      // Handle legacy documents without type field (treat as address)
      if (!data.type) {
        return {
          type: 'address',
          targetAddress: data.targetAddress,
          watchedAt: data.watchedAt,
          notifyOnComment: data.notifyOnComment ?? true,
          notifyOnVote: data.notifyOnVote ?? false
        };
      }
      return data;
    });
  } catch (error) {
    console.error('Error getting watched items:', error);
    return [];
  }
};

/**
 * Get watched coins for the current user
 * @returns {Promise<Array<{coinId, coinName, coinSymbol, ...}>>}
 */
export const getWatchedCoins = async () => {
  const items = await getWatchedItems();
  return items.filter(item => item.type === 'coin');
};

/**
 * Add a coin to watch list via Cloud Function
 * @param {string} coinId - CoinGecko coin ID
 * @param {string} coinName - Coin name
 * @param {string} coinSymbol - Coin symbol
 * @param {string} coinImage - Coin image URL (optional)
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const addWatchedCoin = async (coinId, coinName, coinSymbol, coinImage = null) => {
  if (!auth.currentUser) {
    return { success: false, error: 'You must be signed in to watch coins' };
  }

  try {
    const result = await manageWatchingFn({
      op: 'add',
      type: 'coin',
      coinId,
      coinName,
      coinSymbol,
      coinImage
    });
    return result.data;
  } catch (error) {
    console.error('Error adding watched coin:', error);
    const errorMessage = error.message || 'Failed to add coin';
    return { success: false, error: errorMessage };
  }
};

/**
 * Remove a coin from watch list via Cloud Function
 * @param {string} coinId - CoinGecko coin ID
 * @returns {Promise<{success: boolean}>}
 */
export const removeWatchedCoin = async (coinId) => {
  if (!auth.currentUser) {
    throw new Error('You must be signed in to remove watched coins');
  }

  try {
    const result = await manageWatchingFn({
      op: 'remove',
      type: 'coin',
      coinId
    });
    return result.data;
  } catch (error) {
    console.error('Error removing watched coin:', error);
    throw error;
  }
};

/**
 * Check if a coin is being watched
 * @param {string} coinId - CoinGecko coin ID
 * @param {Array} watchedItems - Current watched items
 * @returns {boolean}
 */
export const isWatchingCoin = (coinId, watchedItems = []) => {
  return watchedItems.some(item => item.type === 'coin' && item.coinId === coinId);
};

/**
 * Validate if an address can be added to watch list
 * Performs local validation before calling cloud function
 * @param {string} address - The address to validate
 * @param {Array} currentWatchedItems - Current watched items (addresses + coins)
 * @param {boolean} inWatchingTab - If in watching tab or not (default false)
 * @returns {{valid: boolean, error?: string}}
 */
export const validateWatchAddress = (address, currentWatchedItems = [], inWatchingTab = false) => {
  if (!address || !address.trim()) {
    return { valid: false, error: 'Please enter an address' };
  }

  const trimmed = address.trim();

  // Basic Ethereum address validation
  if (!/^0x[a-fA-F0-9]{40}$/.test(trimmed)) {
    return { valid: false, error: 'Invalid Ethereum address format' };
  }

  const normalized = trimmed.toLowerCase();

  // Get watched addresses from items (handle both old array of strings and new array of objects)
  const watchedAddresses = currentWatchedItems
    .map(item => typeof item === 'string' ? item : item.targetAddress)
    .filter(Boolean)
    .map(a => a.toLowerCase());

  // Check if already watching
  if (watchedAddresses.includes(normalized)) {
    return { valid: false, error: 'This address is already being watched' };
  }

  // Check limit (total items)
  if (currentWatchedItems.length >= MAX_WATCHED_ITEMS) {
    const error = inWatchingTab
      ? `Maximum ${MAX_WATCHED_ITEMS} watched items allowed. Remove one to add another.`
      : `Maximum ${MAX_WATCHED_ITEMS} watched items allowed. Go to "Settings" → "Watching" to manage them.`;
    return { valid: false, error };
  }

  return { valid: true };
};

/**
 * Validate if a coin can be added to watch list
 * @param {string} coinId - CoinGecko coin ID
 * @param {Array} currentWatchedItems - Current watched items (addresses + coins)
 * @param {boolean} inWatchingTab - If in watching tab or not (default false)
 * @returns {{valid: boolean, error?: string}}
 */
export const validateWatchCoin = (coinId, currentWatchedItems = [], inWatchingTab = false) => {
  if (!coinId || !coinId.trim()) {
    return { valid: false, error: 'Coin ID is required' };
  }

  // Check if already watching
  if (isWatchingCoin(coinId, currentWatchedItems)) {
    return { valid: false, error: 'This coin is already being watched' };
  }

  // Check limit (total items)
  if (currentWatchedItems.length >= MAX_WATCHED_ITEMS) {
    const error = inWatchingTab
      ? `Maximum ${MAX_WATCHED_ITEMS} watched items allowed. Remove one to add another.`
      : `Maximum ${MAX_WATCHED_ITEMS} watched items allowed. Go to "Settings" → "Watching" to manage them.`;
    return { valid: false, error };
  }

  return { valid: true };
};

/**
 * Get user's notification preferences from private subcollection
 * @returns {Promise<{inbox: boolean, email: boolean}>}
 */
export const getNotificationPreferences = async () => {
  if (!auth.currentUser) {
    return { inbox: true, email: false }; // Default
  }

  try {
    const uid = auth.currentUser.uid;
    // Private settings stored at: addresses/{uid}/private/settings
    const settingsRef = doc(db, 'addresses', uid, 'private', 'settings');
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists() && settingsDoc.data().notificationPreferences) {
      return settingsDoc.data().notificationPreferences;
    }

    // Default: inbox only
    return { inbox: true, email: false };
  } catch (error) {
    console.error('Error getting notification preferences:', error);
    return { inbox: true, email: false };
  }
};

/**
 * Update user's notification preferences in private subcollection
 * @param {{inbox: boolean, email: boolean}} preferences
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateNotificationPreferences = async (preferences) => {
  if (!auth.currentUser) {
    return { success: false, error: 'You must be signed in' };
  }

  // Validate at least one method is enabled
  if (!preferences.inbox && !preferences.email) {
    return { success: false, error: 'At least one notification method must be enabled' };
  }

  try {
    const uid = auth.currentUser.uid;
    // Private settings stored at: addresses/{uid}/private/settings
    const settingsRef = doc(db, 'addresses', uid, 'private', 'settings');
    const settingsDoc = await getDoc(settingsRef);

    const data = {
      notificationPreferences: {
        inbox: Boolean(preferences.inbox),
        email: Boolean(preferences.email)
      },
      updatedAt: serverTimestamp()
    };

    if (settingsDoc.exists()) {
      await updateDoc(settingsRef, data);
    } else {
      await setDoc(settingsRef, data);
    }

    return { success: true };
  } catch (error) {
    console.error('Error updating notification preferences:', error);
    return { success: false, error: error.message || 'Failed to update preferences' };
  }
};

/**
 * Update user's display name via direct Firestore write
 * Firestore rules enforce max 50 character limit
 * @param {string} displayName - The new display name (max 50 chars)
 * @returns {Promise<{success: boolean, displayName?: string, error?: string}>}
 */
export const updateDisplayName = async (displayName) => {
  if (!auth.currentUser) {
    return { success: false, error: 'You must be signed in' };
  }

  // Client-side validation (also enforced by Firestore rules)
  const trimmed = (displayName || '').trim();
  if (trimmed.length === 0) {
    return { success: false, error: 'Display name is required' };
  }
  if (trimmed.length > MAX_DISPLAY_NAME_LENGTH) {
    return { success: false, error: `Display name must be ${MAX_DISPLAY_NAME_LENGTH} characters or less` };
  }

  try {
    const uid = auth.currentUser.uid;
    const userRef = doc(db, 'addresses', uid);

    await updateDoc(userRef, {
      displayName: trimmed,
      updatedAt: serverTimestamp()
    });

    return { success: true, displayName: trimmed };
  } catch (error) {
    console.error('Error updating display name:', error);
    const errorMessage = error.message || 'Failed to update display name';
    return { success: false, error: errorMessage };
  }
};
